import { Highlight } from 'prism-react-renderer';
import type { PrismTheme } from 'prism-react-renderer';
import { memo, useMemo } from 'react';
import { cn } from '../../lib/cn';
import { languageFor, parsePatch } from '../../lib/diff';
import type { Finding } from '../../types/api';
import { SEV_DOT } from '../ui/Badges';

/** Colours come from CSS (.token.*) so highlighting follows the light/dark theme. */
const THEME: PrismTheme = { plain: { color: 'inherit', backgroundColor: 'transparent' }, styles: [] };

const CodeLine = memo(function CodeLine({ code, language }: { code: string; language: string }) {
  return (
    <Highlight code={code || ' '} language={language} theme={THEME}>
      {({ tokens, getTokenProps }) => (
        <>
          {(tokens[0] ?? []).map((token, i) => (
            <span key={i} {...getTokenProps({ token })} />
          ))}
        </>
      )}
    </Highlight>
  );
});

export function DiffViewer({ path, patch, findings = [] }: { path: string; patch: string; findings?: Finding[] }) {
  const lines = useMemo(() => parsePatch(patch), [patch]);
  const language = languageFor(path);
  const byLine = useMemo(() => {
    const m = new Map<number, Finding[]>();
    for (const f of findings) if (f.line !== undefined) m.set(f.line, [...(m.get(f.line) ?? []), f]);
    return m;
  }, [findings]);

  return (
    <div className="overflow-x-auto font-mono text-xs leading-5" role="table" aria-label={`Diff of ${path}`}>
      <div className="min-w-max">
        {lines.map((l, i) => {
          if (l.type === 'hunk') {
            return (
              <div key={i} role="row" className="bg-accent/5 px-3 py-1 text-muted">
                {l.content}
              </div>
            );
          }
          if (l.type === 'meta') {
            return (
              <div key={i} role="row" className="px-3 text-faint">
                {l.content}
              </div>
            );
          }
          const marks = l.newLine !== undefined ? byLine.get(l.newLine) : undefined;
          const sign = l.type === 'add' ? '+' : l.type === 'del' ? '−' : ' ';
          return (
            <div
              key={i}
              role="row"
              className={cn(
                'flex',
                l.type === 'add' && 'bg-add/10',
                l.type === 'del' && 'bg-del/10',
                marks && 'outline outline-1 -outline-offset-1 outline-warn/50',
              )}
            >
              <span role="cell" className="w-12 shrink-0 select-none border-r border-line/60 pr-2 text-right text-faint">
                {l.oldLine ?? ''}
              </span>
              <span role="cell" className="relative w-12 shrink-0 select-none border-r border-line/60 pr-2 text-right text-faint">
                {marks && (
                  <span
                    className={cn('absolute left-1.5 top-1/2 h-2 w-2 -translate-y-1/2 rounded-full', SEV_DOT[marks[0]!.severity])}
                    title={marks.map((m) => m.title).join('\n')}
                  />
                )}
                {l.newLine ?? ''}
              </span>
              <span
                role="cell"
                aria-label={l.type === 'add' ? 'added' : l.type === 'del' ? 'removed' : 'unchanged'}
                className={cn('w-6 shrink-0 select-none text-center', l.type === 'add' ? 'text-add' : l.type === 'del' ? 'text-del' : 'text-faint')}
              >
                {sign}
              </span>
              <span role="cell" className="whitespace-pre pr-6">
                <CodeLine code={l.content} language={language} />
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
